import {
  ServerToClientCallMessage,
  AivyInterviewStatus,
  ClientToServerCallMessage,
  ByteFormat,
  Encoding,
  StartInterview
} from 'features/call/call-proto-generated'
import { callAnalyticsService } from './call-analytics.service'
import { callWsStateService } from './call-ws-state.service'
import { createEvent, sample } from 'effector'
import { callWsService } from './call-ws.service'
import { ToggleableUiElement } from 'features/togglable-device'
import { InterviewStageEnum } from 'consts/company-alias.consts'
import { NavigateOptions } from '@tanstack/react-router'
import { router } from 'router'
import { InterviewFinishInitiator } from 'api/endpoints/openapi-generated.schemas'
import {
  interviewFixturesService,
  State
} from '../../interview-fixtures.service'
import { getIsMobile } from 'hooks/use-is-mobile'
import { callUserMicCaptureService } from './call-user-mic-capture.service'
import { callAivyStateService } from './call-aivy-state.service'

export type CallStrategy = 'heygen' | '11labs' | 'chat'

class CallDialogService {
  strategy: CallStrategy = 'heygen'

  readonly doFinishCallByCandidate = createEvent<void>()

  private callFinishInitiator: InterviewFinishInitiator =
    InterviewFinishInitiator.interviewer

  readonly transcriptPanel = new ToggleableUiElement('transcript', void 0, true)

  constructor() {
    callWsStateService.callStarted.watch((): void => {
      callAnalyticsService.callStarted()
      callAnalyticsService.interviewUserConnected()
      callWsService.setShouldReconnect(true)
    })

    this.doFinishCallByCandidate.watch(() => {
      this.callFinishInitiator = InterviewFinishInitiator.candidate
      this.handleFinishCall(true)
    })

    sample({
      clock: callWsService.wsOpened,
      source: interviewFixturesService.$state,
      filter: Boolean,
      fn: this.getStartCallMessage,
      target: callWsService.sendMessageFx
    })
  }

  private getStartCallMessage = ({
    vacancy,
    interview,
    user
  }: State): Uint8Array => {
    const msg: StartInterview = {
      isMobile: getIsMobile(),
      interruptionDisabled: true,
      vacancyId: vacancy.id,
      interviewId: interview.id,
      userId: user.id,
      config: {
        sampleRate: callUserMicCaptureService.getCurrentSampleRate(),
        byteFormat: ByteFormat.PCM_16BIT,
        encoding: Encoding.LINEAR16
      },
      isReconnect: callWsService.isSentAnyMessagesInThisHeap
    }

    return ClientToServerCallMessage.encode({
      startInterviewMessage: msg
    }).finish()
  }

  private handleWsCallStarted = (rs: ServerToClientCallMessage): void => {
    const { callStartedMessage } = rs
    if (!callStartedMessage) return

    callWsStateService.callStarted(callStartedMessage.callId)
  }

  private handleFinishCall = (immediately: boolean): void => {
    callWsStateService.callEndReceived()

    if (immediately) {
      this.redirectOnCallEnd()
    } else {
      const isChat = this.strategy === 'chat'
      const timerTime = isChat ? 5_000 : 45_000

      // timeout for chat mode (when the final replica is not pronounced)
      //  or in case something will go wrong in voice / video mode
      const timer = window.setTimeout(() => {
        this.redirectOnCallEnd()
      }, timerTime)

      if (!isChat) {
        const sub = callAivyStateService.$aivyState.watch((state) => {
          if (state !== 'listen-user') return

          if (window.isDevMode) {
            console.log(
              `handleFinishCall: aivy state is ${state} -> redirect to results in 2s`
            )
          }

          sub.unsubscribe()
          window.clearTimeout(timer)

          window.setTimeout(() => {
            this.redirectOnCallEnd()
          }, 2_000)
        })
      }
    }
  }

  private redirectOnCallEnd = (): void => {
    callWsService.setShouldReconnect(false)
    callWsStateService.showResults()
    callWsService.doCloseConnection()
    callAnalyticsService.callFinished()

    void router.navigate({
      search: {
        stage: InterviewStageEnum.Results,
        initiator: this.callFinishInitiator
      }
    } as NavigateOptions)
  }

  private handleWsCallFinished = (rs: ServerToClientCallMessage): void => {
    const { aivyInterviewStatusAttachments } = rs.textResponseMessage ?? {}
    if (!aivyInterviewStatusAttachments) return

    const [status] = aivyInterviewStatusAttachments
    if (
      status &&
      status.interviewStatus ===
        AivyInterviewStatus.AIVY_INTERVIEW_STATUS_FINISHED
    ) {
      this.handleFinishCall(false)
    }
  }

  private handleWsMessage = (msg: ServerToClientCallMessage): void => {
    this.handleWsCallStarted(msg)
    this.handleWsCallFinished(msg)
  }

  subscribe = (): VoidFunction => {
    return callWsService.autoSubscribeToWsMsgOnOpen(
      'call-dialog-service',
      this.handleWsMessage
    )
  }
}

export const callDialogService = new CallDialogService()
